import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowRight, Lock, User, KeyRound } from 'lucide-react';
import { api } from '../lib/api';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotUsername, setForgotUsername] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login } = useAuth();

  const handleForgotPassword = async () => {
    if (!forgotUsername.trim()) {
      toast.error('Ingresa tu usuario');
      return;
    }
    setForgotLoading(true);
    try {
      await api.forgotPassword(forgotUsername.trim());
      toast.success('Password restablecido a casmu2026');
      setShowForgot(false);
      setForgotUsername('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al restablecer');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      await login(username, password);
      toast.success('Bienvenido');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : 'Error al iniciar sesion';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - BMW M Series Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#00205B]">
        <div className="absolute inset-0 flex flex-col items-center justify-center px-12">
          <div className="w-24 h-24 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center mb-8">
            <span className="text-4xl font-black text-white tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>DC</span>
          </div>
          <h2 className="text-3xl font-bold text-white tracking-tight text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>Departamento Comercial</h2>
          <p className="text-white/60 text-sm mt-3 text-center">Sistema de Gestion de Ventas</p>
          <div className="flex gap-2 mt-8">
            <div className="w-16 h-1 rounded-full bg-[#0066B1]" />
            <div className="w-16 h-1 rounded-full bg-[#E3001B]" />
            <div className="w-16 h-1 rounded-full bg-[#00205B]" />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 m-stripe" />
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-sm">
          {/* Logo */}
          <div className="flex flex-col items-center mb-10">
            <div className="w-16 h-16 rounded-md bg-[#00205B] flex items-center justify-center mb-4">
              <span className="text-white text-2xl font-extrabold" style={{ fontFamily: 'Outfit, sans-serif' }}>DC</span>
            </div>
            <h1 className="text-xl font-bold text-[#0A0A0A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Departamento Comercial</h1>
            <p className="text-[13px] text-gray-500 mt-1">Sistema de Gestion de Ventas</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <div className="relative">
                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="form-input pl-11"
                  placeholder="Usuario o Ficha CASMU"
                  data-testid="login-username"
                />
              </div>
            </div>

            <div>
              <div className="relative">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input pl-11"
                  placeholder="Password"
                  data-testid="login-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2 py-3"
              data-testid="login-submit"
            >
              {loading ? 'Ingresando...' : (
                <>
                  Ingresar
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setShowForgot(true)}
              className="text-[13px] text-[#0082D2] font-semibold hover:underline"
              data-testid="forgot-password-link"
            >
              Olvidaste tu password?
            </button>
          </div>

          {showForgot && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" data-testid="forgot-password-modal">
              <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#0066B1]/10 flex items-center justify-center">
                    <KeyRound size={20} className="text-[#0066B1]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0A0A0A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Restablecer password</h3>
                    <p className="text-xs text-gray-500">Tu password sera casmu2026. Requiere aprobacion del auditor.</p>
                  </div>
                </div>
                <div className="relative mb-4">
                  <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={forgotUsername}
                    onChange={(e) => setForgotUsername(e.target.value)}
                    className="form-input pl-11"
                    placeholder="Usuario o Ficha CASMU"
                    data-testid="forgot-password-username"
                    autoFocus
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowForgot(false); setForgotUsername(''); }}
                    className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                    data-testid="forgot-password-cancel"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={forgotLoading}
                    className="flex-1 btn-primary py-2.5"
                    data-testid="forgot-password-submit"
                  >
                    {forgotLoading ? 'Restableciendo...' : 'Restablecer'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 text-center">
            <p className="text-[13px] text-gray-500">
              No tenes cuenta?{' '}
              <Link to="/register" className="text-[#0082D2] font-semibold hover:underline" data-testid="register-link">
                Registrate
              </Link>
            </p>
          </div>

          <div className="mt-6 py-3 border-t border-gray-200 text-center">
            <span className="text-[10px] text-gray-400 tracking-[0.3em] font-medium">MEBS</span>
          </div>
        </div>
      </div>
    </div>
  );
}
